'use client'

/**
 * Client-side WAPI service (composition-root binding for the standalone wapi-client calls).
 *
 * Built **once** from `config.wapi` — `urlRoot` and the federated `credentials` are bound at
 * construction — and hung on `getClientEnv().services.wapi`. Each method takes the **dynamic
 * user token per call** (the token comes from the localStorage mirror at call time, never from
 * config). Methods keep the promise/throw semantics of the underlying `wapi-client`
 * functions rather than returning a true-myth `Result` (ui-react parity); converting them
 * would ripple into every call site.
 */
import type { ClientWapiConfig } from '@/config/schema'

import {
    federatedLogin,
    getUserInfo,
    identifyRequest,
    logoutRequest,
    refreshRequest,
    type FederatedSession,
    type UserInfo,
} from './wapi-client'

/**
 * Client WAPI service. `urlRoot` + federated `credentials` are bound at construction; every
 * method takes the dynamic user token per call. Mirrors how `services.datadog` is wired.
 */
export interface ClientWapiService {
    /** Exchange a federated provider credential (Google) for a WAPI session (`auth.json`). */
    exchangeFederated(provider: 'google', providerToken: string): Promise<FederatedSession>
    /** Refresh an expired cognito session token (`refresh.json`). */
    refresh(opts: { email: string; token: string; sessionId?: string }): Promise<FederatedSession>
    /** Fetch the logged-in user's profile for the navbar greeting (`userinfo.json`). */
    getUserInfo(token: string): Promise<UserInfo>
    /** Invalidate the session server-side (`logout.json`). */
    logout(token: string): Promise<void>
    /** POST a `user:action:identify` event (`events.json`). */
    identify(token: string, data: Record<string, unknown>): Promise<void>
}

/**
 * Creates the client WAPI service from the client-safe `wapi` config. Pure + synchronous —
 * binds `urlRoot` + `credentials` and delegates to the existing `wapi-client` functions.
 */
export function createClientWapiService(config: ClientWapiConfig): ClientWapiService {
    const { urlRoot, credentials } = config

    return {
        exchangeFederated: (provider, providerToken) =>
            federatedLogin({ urlRoot, credentials, provider, token: providerToken }),
        refresh: (opts) => refreshRequest({ urlRoot, ...opts }),
        getUserInfo: (token) => getUserInfo({ urlRoot, token }),
        logout: (token) => logoutRequest(urlRoot, token),
        identify: (token, data) => identifyRequest({ urlRoot, token, data }),
    }
}
